import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const location = useLocation();

  const isDashboardRoute = useMemo(() => {
    const pathname = location.pathname;
    return pathname === '/dashboard' || pathname === '/';
  }, [location.pathname]);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, reg) {
      if (reg) {
        // Check for updates periodically (hourly)
        setInterval(() => reg.update(), 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error', error);
    },
  });

  useEffect(() => {
    if (!needRefresh || !isDashboardRoute) return;

    updateServiceWorker(true);
    setNeedRefresh(false);
  }, [needRefresh, isDashboardRoute, setNeedRefresh, updateServiceWorker]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const close = () => {
    setShowInstallPrompt(false);
    setNeedRefresh(false);
  };

  if (!showInstallPrompt && !needRefresh) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-50">
      {needRefresh && (
        <div className="bg-white border rounded-lg shadow-lg p-4 mb-2 max-w-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">{t('pwa.newContentAvailable')}</span>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Button
            onClick={() => updateServiceWorker(true)}
            size="sm"
            className="w-full"
          >
            {t('pwa.reload')}
          </Button>
        </div>
      )}

      {showInstallPrompt && (
        <div className="bg-white border rounded-lg shadow-lg p-4 max-w-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">{t('pwa.installTitle')}</span>
            <button
              onClick={() => setShowInstallPrompt(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-gray-600 mb-3">{t('pwa.installDescription')}</p>
          <Button
            onClick={handleInstallClick}
            size="sm"
            className="w-full"
          >
            <Download className="h-4 w-4 mr-2" />
            {t('pwa.installCta')}
          </Button>
        </div>
      )}
    </div>
  );
}
